/**
 * @fileoverview Layout mobile-first du portail citoyen.
 */

import { useState } from 'react';
import { Link, Outlet, NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CircleHelp, Clock3, Headphones, Linkedin, LogOut, Search, X as XIcon } from 'lucide-react';
import { Button, LanguageSwitcher, cn } from '@gnpc/ui';

const PUBLIC_PATHS = new Set(['/', '/bon-a-savoir']);

const PORTAL_NAV_ITEMS = [
  { to: '/recherche', label: 'Recherche', icon: <Search />, requiresSession: true },
  { to: '/historique', label: 'Historique', icon: <Clock3 />, requiresSession: true },
  { to: '/bon-a-savoir', label: 'Bon à savoir', icon: <CircleHelp />, requiresSession: false },
  { to: '/assistance', label: 'Assistance', icon: <Headphones />, requiresSession: true },
] as const;

const FOOTER_LINKS = [
  { to: '/', label: 'Accueil' },
  { to: '/bon-a-savoir', label: 'Bon à savoir' },
  { to: '/recherche', label: 'Recherche' },
  { to: '/historique', label: 'Historique' },
  { to: '/assistance', label: 'Assistance' },
] as const;

const PORTAL_LANGUAGES = [
  { code: 'fr', shortLabel: 'FR', nativeName: 'Français' },
  { code: 'en', shortLabel: 'EN', nativeName: 'English' },
  { code: 'es', shortLabel: 'ES', nativeName: 'Español' },
  { code: 'de', shortLabel: 'DE', nativeName: 'Deutsch' },
] as const;

type PortalLanguage = (typeof PORTAL_LANGUAGES)[number]['code'];

export function PortalLayout(): JSX.Element {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === '/';
  const isFullBleedPage = isHome || pathname === '/recherche' || pathname === '/assistance';
  const hasPortalSession = !PUBLIC_PATHS.has(pathname);
  const visibleNavItems = PORTAL_NAV_ITEMS.filter(
    (item) => hasPortalSession || !item.requiresSession,
  );

  return (
    <div className="flex min-h-dvh flex-col bg-slate-50">
      <header className="border-quipux-navy-950 bg-quipux-navy-950 border-b">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3">
          <NavLink
            to={hasPortalSession ? '/recherche' : '/'}
            className="inline-flex items-center"
            aria-label={hasPortalSession ? 'Retour à la recherche' : "Retour à l'accueil"}
          >
            <img src={`${import.meta.env.BASE_URL}brand/logo_quipux_blanc.png`} alt="Quipux Afrique" className="h-10 w-auto" />
          </NavLink>

          <div className="flex items-center gap-1">
            <nav aria-label="Navigation portail" className="flex items-center gap-1">
              {visibleNavItems.map((item) => (
                <PortalNavLink key={item.to} to={item.to} label={item.label} icon={item.icon} />
              ))}
            </nav>
            <PortalLanguageSwitcher surface="dark" />
            {hasPortalSession ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-10 text-white/80 hover:bg-white/10 hover:text-white"
                onClick={() => navigate('/')}
              >
                <LogOut className="size-4" />
                {/* `sr-only` plutôt que `hidden` : en mobile le bouton est
                  * icône-seule et perdrait sinon tout nom accessible
                  * (violation axe button-name). */}
                <span className="sr-only sm:not-sr-only">Se déconnecter</span>
              </Button>
            ) : null}
          </div>
        </div>
      </header>

      <main
        className={cn('flex-1', isFullBleedPage ? undefined : 'mx-auto w-full max-w-5xl px-4 py-6')}
      >
        <Outlet />
      </main>

      <PortalFooter />
    </div>
  );
}

interface PortalNavLinkProps {
  to: string;
  label: string;
  icon: React.ReactNode;
}

function PortalNavLink({ to, label, icon }: PortalNavLinkProps): JSX.Element {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'inline-flex h-10 items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors',
          isActive ? 'bg-white/15 text-white' : 'text-white/80 hover:bg-white/10 hover:text-white',
        )
      }
    >
      <span className="[&>svg]:size-4">{icon}</span>
      {/* `sr-only` plutôt que `hidden` : en mobile le lien est icône-seule
        * et perdrait sinon tout nom accessible (violation axe link-name). */}
      <span className="sr-only sm:not-sr-only">{label}</span>
    </NavLink>
  );
}

function isPortalLanguage(value: string | null | undefined): value is PortalLanguage {
  return PORTAL_LANGUAGES.some((option) => option.code === value);
}

interface PortalLanguageSwitcherProps {
  surface: 'dark' | 'light';
}

/**
 * Sélecteur de langue du portail — état en MÉMOIRE uniquement.
 *
 * `localStorage`/`sessionStorage` sont proscrits sur le portail citoyen
 * (Guide frontend : la survie d'état passe par l'URL ou la session). La
 * langue retombe donc sur « fr » au rechargement ; au branchement backend,
 * la préférence sera portée par le profil de session (cookie serveur).
 */
function PortalLanguageSwitcher({ surface }: PortalLanguageSwitcherProps): JSX.Element {
  const [language, setLanguage] = useState<PortalLanguage>('fr');

  return (
    <LanguageSwitcher
      value={language}
      options={PORTAL_LANGUAGES}
      onValueChange={(value) => {
        if (isPortalLanguage(value)) setLanguage(value);
      }}
      surface={surface}
    />
  );
}

function PortalFooter(): JSX.Element {
  // Pastille de disponibilité pilotée par l'état mock : les pages simulent
  // leurs états via l'URL (`?state=error`, convention des maquettes sans
  // backend). Tant que l'URL porte un état d'erreur, le pied de page
  // reflète la perturbation au lieu d'afficher un « disponible » mensonger.
  const [searchParams] = useSearchParams();
  const isDisrupted = searchParams.get('state') === 'error';

  return (
    <footer className="border-border mt-16 rounded-t-3xl border-t bg-white">
      <div className="mx-auto max-w-5xl px-4 py-10 md:py-12">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="space-y-6">
              <Link to="/" className="inline-flex" aria-label="Retour à l'accueil">
                <img src={`${import.meta.env.BASE_URL}brand/logo_quipux.png`} alt="Quipux Afrique" className="h-10 w-auto" />
              </Link>

              <nav aria-label="Liens de pied de page" className="flex flex-wrap gap-x-7 gap-y-3">
                {FOOTER_LINKS.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="text-quipux-navy-900 hover:text-quipux-orange-600 text-sm font-medium transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>

            <div className="text-quipux-navy-700 flex items-center gap-3">
              <PortalLanguageSwitcher surface="light" />
              {/* URLs provisoires — à confirmer avec la communication Quipux
                * avant mise en production. */}
              <a
                href="https://x.com/quipuxafrique"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Quipux Afrique sur X (ouvre un nouvel onglet)"
                className="hover:border-border hover:text-quipux-orange-600 inline-flex size-9 items-center justify-center rounded-md border border-transparent transition-colors"
              >
                <XIcon className="size-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/quipux-afrique"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Quipux Afrique sur LinkedIn (ouvre un nouvel onglet)"
                className="hover:border-border hover:text-quipux-orange-600 inline-flex size-9 items-center justify-center rounded-md border border-transparent transition-colors"
              >
                <Linkedin className="size-5" />
              </a>
            </div>
          </div>

          <div className="border-quipux-navy-200 border-t border-dotted" />

          <div className="text-muted-foreground flex flex-col gap-4 text-sm md:flex-row md:items-center md:justify-between">
            <p>© 2026 Quipux Afrique</p>
            <div className="border-border text-quipux-navy-900 inline-flex w-fit items-center gap-2 rounded-full border bg-white px-4 py-2">
              <span
                className={cn(
                  'size-2 rounded-full',
                  isDisrupted ? 'bg-warning' : 'bg-status-paid',
                )}
                aria-hidden="true"
              />
              <span className="font-medium">
                {isDisrupted ? 'Service perturbé' : 'Service disponible'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
